import React from 'react';
import AssistantBotRow from '../shells/AssistantBotRow';
import UserReplyRow from '../shells/UserReplyRow';
import {
  formAgreeFollowUpKeys,
  formDeclineFollowUpKeys,
  isAgreeChoice,
  stepQuestionKey,
  workHistoryConfirmYesFollowUpKeys,
  workHistoryCorrectionFollowUpKeys,
} from './healthInfoZuidoSteps';
import type { HealthInfoZuidoStep } from './healthInfoZuidoTypes';
import type { useHealthInfoZuidoFlow } from './useHealthInfoZuidoFlow';

type Flow = ReturnType<typeof useHealthInfoZuidoFlow>;

const CONSENT_CHOICE_STEPS: HealthInfoZuidoStep[] = ['agreeForm31', 'agreeForm32'];
const WORK_HISTORY_CHOICE_STEPS: HealthInfoZuidoStep[] = ['confirmWorkHistory', 'workHistoryCorrection'];

function ConsentChoiceFollowUp({
  stepId,
  agreed,
  t,
}: {
  stepId: HealthInfoZuidoStep;
  agreed: boolean;
  t: Flow['t'];
}) {
  if (!agreed) {
    return (
      <>
        {formDeclineFollowUpKeys().map((key) => (
          <AssistantBotRow key={key} animateIn>
            {t(key)}
          </AssistantBotRow>
        ))}
      </>
    );
  }
  if (stepId === 'agreeForm31') {
    return <AssistantBotRow animateIn>{t('submitDocuments.healthInfoZuido.formAgreeThankYou')}</AssistantBotRow>;
  }
  return (
    <>
      {formAgreeFollowUpKeys().map((key) => (
        <AssistantBotRow key={key} animateIn>
          {t(key)}
        </AssistantBotRow>
      ))}
    </>
  );
}

function WorkHistoryChoiceFollowUp({
  stepId,
  rawValue,
  t,
}: {
  stepId: HealthInfoZuidoStep;
  rawValue: string;
  t: Flow['t'];
}) {
  if (stepId === 'confirmWorkHistory') {
    if (isAgreeChoice(rawValue)) {
      return (
        <>
          {workHistoryConfirmYesFollowUpKeys().map((key) => (
            <AssistantBotRow key={key} animateIn>
              {t(key)}
            </AssistantBotRow>
          ))}
        </>
      );
    }
    return (
      <AssistantBotRow animateIn>
        {t('submitDocuments.healthInfoZuido.workHistory.correctionPrompt')}
      </AssistantBotRow>
    );
  }
  if (stepId === 'workHistoryCorrection') {
    return (
      <>
        {workHistoryCorrectionFollowUpKeys().map((key) => (
          <AssistantBotRow key={key} animateIn>
            {t(key)}
          </AssistantBotRow>
        ))}
      </>
    );
  }
  return null;
}

export default function HealthInfoZuidoChatThread({ flow }: { flow: Flow }) {
  const {
    t,
    committed,
    formDeclined,
    currentStep,
    assistantQuestionVisible,
    canShowUndo,
    handleUndo,
    undoLabel,
  } = flow;
  const committedStepIds = new Set(committed.map((c) => c.stepId));
  const lastCommittedStepId =
    committed.length > 0 ? committed[committed.length - 1].stepId : null;

  return (
    <>
      {committed.map((c, i) => (
        <React.Fragment key={`${c.stepId}-${i}`}>
          {CONSENT_CHOICE_STEPS.includes(c.stepId) && (
            <AssistantBotRow animateIn>{t(stepQuestionKey(c.stepId))}</AssistantBotRow>
          )}
          {WORK_HISTORY_CHOICE_STEPS.includes(c.stepId) && (
            <AssistantBotRow animateIn>
              {c.stepId === 'confirmWorkHistory'
                ? t('submitDocuments.healthInfoZuido.workHistory.confirmQuestion')
                : t('submitDocuments.healthInfoZuido.workHistory.correctionPrompt')}
            </AssistantBotRow>
          )}
          <UserReplyRow
            displayText={c.displayText}
            showUndo={canShowUndo && c.stepId === lastCommittedStepId}
            onUndo={() => void handleUndo()}
            undoLabel={undoLabel}
          />
          {CONSENT_CHOICE_STEPS.includes(c.stepId) && (
            <ConsentChoiceFollowUp stepId={c.stepId} agreed={isAgreeChoice(c.rawValue)} t={t} />
          )}
          {WORK_HISTORY_CHOICE_STEPS.includes(c.stepId) && (
            <WorkHistoryChoiceFollowUp stepId={c.stepId} rawValue={c.rawValue} t={t} />
          )}
        </React.Fragment>
      ))}

      {!formDeclined &&
        currentStep?.kind === 'choice' &&
        assistantQuestionVisible &&
        !committedStepIds.has(currentStep.stepId) && (
          <AssistantBotRow animateIn>
            {currentStep.stepId === 'confirmWorkHistory'
              ? t('submitDocuments.healthInfoZuido.workHistory.confirmQuestion')
              : t(stepQuestionKey(currentStep.stepId))}
          </AssistantBotRow>
        )}

      {!formDeclined &&
        currentStep?.kind === 'category' &&
        assistantQuestionVisible &&
        !committedStepIds.has(currentStep.stepId) && (
          <AssistantBotRow animateIn>
            {t('submitDocuments.healthInfoZuido.workHistory.correctionPrompt')}
          </AssistantBotRow>
        )}
    </>
  );
}
